import { useMemo, useState } from "react";
import { MdArrowBack, MdChevronRight } from "react-icons/md";
import { toast } from "react-toastify";

import PhoneNumberInput from "../common/userRegistration/PhoneNumberInput";
import PasswordField from "../common/userRegistration/PasswordField";
import PasswordStrength from "../common/userRegistration/PasswordStrength";
import { isValidEmail, isValidPassword, passwordStrength } from "../../utils/validators";
import { getAlpha2, getCountryName } from "../common/commonUtils/countries";
import { getApiErrorMessage } from "../../utils/errorHandling";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneValid: false,
  password: "",
  confirmPassword: "",
};

const isBlank = (value) => !value || !String(value).trim();

export default function OwnerRegistrationForm({
  businessCountry,
  onSubmit,
  onBack,
  submitting = false,
}) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const countryCode = useMemo(() => {
    const code = getAlpha2(businessCountry || "");
    return code ? code.toUpperCase() : "";
  }, [businessCountry]);

  const countryName = getCountryName(countryCode) || countryCode || "business country";
  const strength = passwordStrength(form.password);

  const errors = {
    firstName: isBlank(form.firstName),
    lastName: isBlank(form.lastName),
    email: !isValidEmail(form.email),
    phone: Boolean(form.phone) && !form.phoneValid,
    password: !isValidPassword(form.password),
    confirmPassword: !form.confirmPassword || form.password !== form.confirmPassword,
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shouldShow = (field) => (showErrors || touched[field]) && errors[field];

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (hasErrors || submitting) return;

    try {
      await onSubmit?.({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone?.trim() || null,
        country: countryCode || businessCountry,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to register this business."));
    }
  };

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="ui-title mb-0">Owner account</h3>
          <span className="text-muted small">Country: {countryName}</span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-md-4">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="owner-first-name">First name</label>
            <input
              id="owner-first-name"
              type="text"
              className={`form-control ${shouldShow("firstName") ? "is-invalid" : ""}`}
              value={form.firstName}
              onChange={(event) => update({ firstName: event.target.value })}
              onBlur={() => markTouched("firstName")}
              autoComplete="given-name"
              disabled={submitting}
            />
            {shouldShow("firstName") && <div className="invalid-feedback">First name is required.</div>}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="owner-last-name">Last name</label>
            <input
              id="owner-last-name"
              type="text"
              className={`form-control ${shouldShow("lastName") ? "is-invalid" : ""}`}
              value={form.lastName}
              onChange={(event) => update({ lastName: event.target.value })}
              onBlur={() => markTouched("lastName")}
              autoComplete="family-name"
              disabled={submitting}
            />
            {shouldShow("lastName") && <div className="invalid-feedback">Last name is required.</div>}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="owner-email">Email</label>
            <input
              id="owner-email"
              type="email"
              className={`form-control ${shouldShow("email") ? "is-invalid" : ""}`}
              value={form.email}
              onChange={(event) => update({ email: event.target.value })}
              onBlur={() => markTouched("email")}
              autoComplete="email"
              disabled={submitting}
            />
            {shouldShow("email") && <div className="invalid-feedback">Enter a valid email.</div>}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Phone</label>
            <div className={`form-control p-0 ${shouldShow("phone") ? "is-invalid" : ""}`}>
              <PhoneNumberInput
                key={countryCode || "default"}
                countryName={countryCode}
                value={form.phone}
                onChange={(data) =>
                  update({
                    phone: data.phone ? "+" + data.phone : "",
                    phoneValid: data.isValid,
                  })
                }
                inputClass="w-100 border-0 shadow-none bg-body"
                buttonClass="btn bg-body border-0"
                disabled={!countryCode || submitting}
              />
            </div>
            {shouldShow("phone") && <div className="text-danger small mt-1">Enter a valid phone number.</div>}
          </div>
        </div>

        <hr className="my-4" />

        <PasswordField
          label="Password"
          name="owner-password"
          value={form.password}
          onChange={(event) => update({ password: event.target.value })}
          onBlur={() => markTouched("password")}
          placeholder="Create a password"
          disabled={submitting}
          invalid={shouldShow("password")}
          invalidText="Must include upper, lower, digit, symbol (8-128 chars)."
        />

        <PasswordField
          label="Confirm password"
          name="owner-confirm-password"
          value={form.confirmPassword}
          onChange={(event) => update({ confirmPassword: event.target.value })}
          onBlur={() => markTouched("confirmPassword")}
          placeholder="Repeat password"
          disabled={submitting}
          invalid={shouldShow("confirmPassword")}
          invalidText="Passwords must match."
        />

        {form.password && <PasswordStrength strength={strength} />}

        <div className="ui-actions mt-4 w-100 justify-content-between">
          <button type="button" className="ui-btn" onClick={onBack} disabled={submitting}>
            <MdArrowBack /> Back
          </button>
          <button
            type="submit"
            className="btn btn-primary rounded-pill d-inline-flex align-items-center gap-2 ms-auto"
            disabled={submitting}
          >
            {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            Submit registration
            <MdChevronRight />
          </button>
        </div>
      </form>
    </section>
  );
}
